// Galerie de photos d'un véhicule (contrat C0 v1.13, kind « photos ») : carte construite par le
// serveur pour chaque résultat de get_photos_vehicule qui porte au moins une photo, sans appel de
// render_visual par le modèle. Lecture tolérante de la spécification ; fonctions pures (testées
// par node --test).
//
// Spécification émise (backend/fichiers/galerie.js) :
//   {version, kind: "photos", visual_type: "photos", title: "Photos du véhicule — <nom> (<immat>)",
//    vehicule: {nom?, immatriculation?, vehicule_id?, fiche: [{label, label_en, valeur}]},
//    photos: [{cle, libelle, libelle_en, lien: "/api/fichiers/<jeton>", type: "image" | "pdf"}],
//    absentes: [{cle, libelle, libelle_en}], expire_le, source, actions}

import { lienFichier } from './fichiers.js';

const str = (v, max = 140) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : typeof v === 'number' && Number.isFinite(v) ? String(v) : '');

/** Spécification de galerie → modèle d'affichage ; null sans photo affichable. */
export function normalizePhotosSpec(raw, now = Date.now()) {
  if (!raw || typeof raw !== 'object' || raw.kind !== 'photos') return null;
  const photos = (Array.isArray(raw.photos) ? raw.photos : [])
    .map((p, i) => {
      const lien = lienFichier(p?.lien);
      if (!lien) return null;
      return { cle: str(p.cle, 60) || `photo-${i + 1}`, libelle: str(p.libelle, 60) || 'Photo', lien, type: p.type === 'pdf' ? 'pdf' : 'image' };
    })
    .filter(Boolean)
    .slice(0, 12);
  if (!photos.length) return null;
  const v = raw.vehicule && typeof raw.vehicule === 'object' ? raw.vehicule : {};
  const fiche = (Array.isArray(v.fiche) ? v.fiche : [])
    .map((f) => ({ label: str(f?.label, 40), valeur: str(f?.valeur ?? f?.value, 80) }))
    .filter((f) => f.label && f.valeur)
    .slice(0, 8);
  const expireLe = Date.parse(raw.expire_le);
  const vehicule = str(v.immatriculation, 40) || str(v.nom, 80);
  return {
    title: str(raw.title) || 'Photos du véhicule',
    nom: str(v.nom, 80),
    immatriculation: str(v.immatriculation, 40),
    fiche,
    photos: photos.map((p) => ({ ...p, alt: vehicule ? `${p.libelle} du véhicule ${vehicule}` : p.libelle })),
    absentes: (Array.isArray(raw.absentes) ? raw.absentes : []).map((a) => str(a?.libelle ?? a, 40)).filter(Boolean).slice(0, 8),
    expireLe: Number.isFinite(expireLe) ? expireLe : null,
    expire: Number.isFinite(expireLe) && expireLe <= now,
    source: raw.source && typeof raw.source === 'object' ? raw.source : null,
  };
}

/** Photos affichables dans la visionneuse (images seulement), dans l'ordre de la galerie. */
export const imagesDeGalerie = (spec) =>
  (spec?.photos || []).filter((p) => p.type === 'image').map(({ lien, libelle, alt }) => ({ lien, libelle, alt, vehicule: spec.immatriculation || spec.nom || '' }));

/** Nom de fichier proposé au téléchargement : « Exterieur-AB-123-CD.jpg ». */
export function nomDeTelechargement(libelle, vehicule = '') {
  const base = `${libelle || 'photo'}${vehicule ? `-${vehicule}` : ''}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || 'photo';
}
